import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Pencil from "../../../../images/pencil.png";
import { useForm } from "react-hook-form";
import { toast } from 'react-toastify';
import { useVendorForm } from "./../VendorFormContext";

const BillingTerms = () => {
  const navigate = useNavigate();
  const { formData, updateFormData, saveVendorData, saveAllChanges, hasUnsavedChanges, isCreating, isSaving, markTabComplete } = useVendorForm();
  // Minimal, robust defaultValues logic
  const billingTermsValue = {
    currency: formData?.billingTerms?.currency ?? '',
    creditLimit: formData?.billingTerms?.creditLimit ?? '',
    creditHold: formData?.billingTerms?.creditHold ?? ''
  };
  const defaultValues = {
    currency: String(billingTermsValue.currency ?? ''),
    creditLimit: String(billingTermsValue.creditLimit ?? ''),
    creditHold: String(billingTermsValue.creditHold ?? '')
  };

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm({
    defaultValues,
    mode: 'onChange',
    shouldUnregister: true,
  });

  // Only reset if defaultValues change from outside (not on every keystroke)
  const prevDefaults = useRef(defaultValues);
  useEffect(() => {
    if (JSON.stringify(prevDefaults.current) !== JSON.stringify(defaultValues)) {
      reset(defaultValues);
      prevDefaults.current = defaultValues;
    }
  }, [defaultValues, reset]);

  // Watch for changes and auto-sync to context for global change detection
  // Skip first sync — resetVendorData already populated context
  const watched = watch();
  const prevWatchedData = useRef(watched);
  const isFirstSync = useRef(true);
  useEffect(() => {
    if (JSON.stringify(prevWatchedData.current) !== JSON.stringify(watched)) {
      if (!isFirstSync.current) {
        updateFormData('billingTerms', watched);
      }
      prevWatchedData.current = watched;
      isFirstSync.current = false;
    }
  }, [watched, updateFormData]);

  const onSubmit = async (data) => {
    // data: { currency, creditLimit, creditHold }
    updateFormData("billingTerms", data);
    saveVendorData("billingTerms", data);
    reset(data); // Reset with current data to clear dirty state
  };

  return (
    <div className="w-full h-full">
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-y-6 mt-5 text-sm font-normal text-text-hint">
        <div className="grid grid-cols-4 gap-3">
          <div className="">
            <label className="">Currency</label>
            <div className={`flex justify-center items-center px-3 py-2 mt-1 border rounded-lg  ${errors.currency ? 'border-red-500' : 'border-neutral-500'}`}>
              <input
                {...register("currency", { required: "Currency is required", maxLength: { value: 3, message: "Currency code must not exceed 3 characters" } })}
                maxLength={3}
                placeholder="Currency"
                className={`outline-none w-full`}
              />
              <img src={Pencil} alt="pencil" />
            </div>
            {errors.currency && <span className="text-danger-500">{errors.currency.message}</span>}
          </div>
        </div>

        <div className="grid grid-cols-4 gap-3">
          <div className="">
            <label className="">Credit Limit</label>
              <input
                {...register("creditLimit", { required: "Credit limit is required", setValueAs: (value) => value === '' ? '' : parseInt(value, 10), min: { value: 0, message: "Must be 0 or more" }, max: { value: 99999999, message: "Value too large" } })}
                placeholder="Credit Limit"
                className={`outline-none w-full text-black appearance-none flex justify-center items-center px-3 py-2 mt-1 border rounded-lg ${errors.creditLimit ? 'border-red-500' : 'border-neutral-500'}`}
                type="number"
                min={0}
                max={99999999}
              />
            {errors.creditLimit && <span className="text-danger-500">{errors.creditLimit.message}</span>}
          </div>
          <div className="">
            <label className="">Credit Hold</label>
              <input
                {...register("creditHold", { required: "Credit hold is required", setValueAs: (value) => value === '' ? '' : parseInt(value, 10), min: { value: 0, message: "Must be 0 or more" }, max: { value: 99999999, message: "Value too large" } })}
                placeholder="Credit Hold"
                className={`outline-none w-full text-black appearance-none flex justify-center items-center px-3 py-2 mt-1 border rounded-lg ${errors.creditHold ? 'border-red-500' : 'border-neutral-500'}`}
                type="number"
                min={0}
                max={99999999}
              />
            {errors.creditHold && <span className="text-danger-500">{errors.creditHold.message}</span>}
          </div>
        </div>
        {/* <div className="grid grid-cols-4 gap-3">
          <div className="flex flex-col col-span-2 ">
            <label htmlFor="document_type" className="text-text-hint mb-1">
              Feedback
            </label>
            <textarea
              {...register("feedback", { required: "Feedback is required" })}
              placeholder="Feedback"
              id="document_type"
              className="focus:outline-none border rounded-lg p-2"
              rows={4}
            />
            {errors.feedback && <span className="text-danger-500">{errors.feedback.message}</span>}
          </div>
        </div> */}
      </form>
      <div className="w-full py-10 px-8 flex justify-end items-center gap-2">
        <button
          type="button"
          onClick={() => navigate("/sales/vendors/details/company-preference")}
          className="text-secondary-700 border border-secondary-700 py-2 px-5 rounded-full text-base bg-white hover:bg-gray-50 transition"
        >
          Previous
        </button>
        {!isCreating && hasUnsavedChanges && (
          <button
            type="button"
            disabled={isSaving}
            onClick={saveAllChanges}
            className="text-white bg-green-600 py-2 px-5 rounded-full text-base hover:bg-green-700 transition disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        )}
        <button
          type="button"
          onClick={handleSubmit((data) => {
            updateFormData("billingTerms", data);
            if (isCreating) markTabComplete("billing-terms");
            navigate("/sales/vendors/details/description");
          })}
          className="text-white bg-secondary-700 py-2 px-5 rounded-full text-base"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default BillingTerms;
